import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { PageHero } from "@/layouts/PageHero";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { Counter } from "@/components/common/Counter";
import { CtaButton } from "@/components/common/CtaButton";
import { DetailCta } from "@/components/detail/DetailCta";
import { whyChooseUs } from "@/data/faq.data";
import { stats } from "@/data/home.data";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { siteConfig } from "@/config/site.config";
import MarketingIllustration from "@/components/common/MarketingIllustration";

/** Concept team — illustrative until real profiles are published. */
const team = [
  { name: "VibeWell Editorial Team", role: "Business Technology Experts", initials: "VW", accent: "#0F766E" },
  { name: "Devan Rao", role: "Engineering Lead", initials: "DR", accent: "#06B6D4" },
  { name: "Iris Novak", role: "Product Strategist", initials: "IN", accent: "#6366F1" },
  { name: "Theo Adeyemi", role: "Full-Stack Engineer", initials: "TA", accent: "#F59E0B" },
];

const transformationStats = [
    {
        value: "AI",
        label: "Automation Solutions",
    },
    {
        value: "ERP",
        label: "Enterprise Platforms",
    },
    {
        value: "8+",
        label: "Industries Served",
    },
    {
        value: "24/7",
        label: "Long-Term Partnership",
    },
];

export default function About() {
  return (
    <>
      <Seo
        title="About"
        description="Learn about VibeWell Software Solutions, a trusted technology solutions company helping businesses accelerate digital transformation through innovative, secure, and scalable software solutions. Discover our journey, values, expertise, and commitment to delivering lasting business value."
        path="/about"
      />
      <PageHero
        eyebrow="About"
        titleLines={["Transforming Businesses", "Through AI Innovation."]}
        description="VibeWell helps organisations modernise operations through AI, intelligent automation and enterprise software that delivers measurable business outcomes."
      >
        <CtaButton to={siteConfig.primaryCta.path} withArrow magnetic>
          Work with us
        </CtaButton>
      </PageHero>

      {/* Story */}
      <Section spacing="compact" className="pt-0">
        <Container>
          <SectionHeading eyebrow="Our Journey" title="Our Mission" />
          <div className="grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
            <div className="space-y-5">
              <Reveal>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Businesses today need more than software—they need intelligent systems that automate work, connect data and accelerate growth. 
                  VibeWell was founded to help organisations embrace AI-driven digital transformation through practical, scalable and secure 
                  technology solutions.
                  From modern websites and enterprise web applications to mobile apps, AI-powered automation, ERP,
                  CRM, and industry-specific software, we partner with organizations to design, develop, and deliver
                  solutions that support long-term growth. Every project is approached with a deep understanding
                  of business objectives, ensuring technology becomes a strategic advantage rather than just another tool.
                </p>
              </Reveal>
              {/* <Reveal delay={0.1}>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Our approach combines strategic thinking, user-centered design, modern software engineering, 
                  and emerging technologies to deliver reliable, secure, and scalable digital solutions. 
                  We work closely with our clients throughout every stage of the project — from discovery and planning 
                  to deployment, training, and long-term support—building trusted partnerships that continue well beyond project delivery.
                </p>
              </Reveal> */}
            </div>
            <MarketingIllustration
              src="/images/about/business-transformation.webp"
              alt="Business Transformation with AI"
            />
          </div>
        </Container>
      </Section>

      {/* Values */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading
            eyebrow="Our Core Values"
            title="Technology with Purpose"
            description="Every engagement is guided by innovation, engineering excellence, measurable business outcomes, and trusted long-term partnerships."
          />
          <motion.div
            variants={staggerContainer(0.06, 0.08)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {whyChooseUs.map(({ title, description, icon: Icon }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                className="group flex gap-4 rounded-[1.25rem] border border-border bg-card p-6 transition-all duration-300 hover:border-primary/20"
              >
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-border bg-border lg:grid-cols-4">
            {transformationStats.map((s) => (
              <div key={s.label} className="bg-card p-8 text-center">
                {/* <Counter
                  value={s.value}
                  className="font-heading text-4xl font-extrabold tracking-tight text-primary sm:text-5xl"
                /> */}
                <p className="font-heading text-4xl font-extrabold tracking-tight text-primary sm:text-5xl">{s.value}</p>
                <p className="mt-2 text-sm text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Team */}
      <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="Our People"
            title="Building AI-Driven Solutions That Deliver Results"
            description="Our team brings together expertise in software engineering, web technologies, mobile application development, cloud platforms, AI, automation, UI/UX design, and digital transformation to help businesses solve real-world challenges."
          />
          {/* <motion.div
            variants={staggerContainer(0.06, 0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
          >
            {team.map((member) => (
              <motion.div
                key={member.name}
                variants={fadeUp}
                data-testid={`team-member-${member.initials.toLowerCase()}`}
                className="rounded-[1.25rem] border border-border bg-card p-6 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl font-heading text-lg font-bold text-white"
                  style={{ background: member.accent }}
                >
                  {member.initials}
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground">{member.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{member.role}</p>
              </motion.div>
            ))}
          </motion.div> */}

          <Reveal>
            <div className="mt-12 flex flex-col items-start gap-4 rounded-[1.5rem] border border-border bg-card p-8 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-heading text-xl font-bold text-foreground">Ready to Build the Future of Your Business?</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  Let's discuss how AI, automation and modern software can transform your operations.
                </p>
                {/* <p className="mt-1.5 text-sm text-muted-foreground">
                  Discover our proven approach to planning, designing, developing, deploying, 
				          and supporting technology solutions that deliver measurable business value.
                </p> */}
              </div>
              <CtaButton to="/process" variant="outline" withArrow className="shrink-0">
                Explore our process
              </CtaButton>
            </div>
          </Reveal>
        </Container>
      </Section>

      <DetailCta />
    </>
  );
}
